import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { getUserDetails, updateUserDetails, updatePassword, getMySubscriptionAndQuota } from '../../api/userApi';
import Modal from '../common/Modal';
import languageOptions from '../../config/languageOptions';
import './Profile.css';
import { UpdatingEmail, UpdatingLanguage, UpdatingNameGenderDOB, UpdatingPassword, UpdatingSubscriptionLevel } from './UpdatingProfile';
import { formatDate } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import LoadingOverlay from '../common/LoadingOverlay';

const stripePromise = loadStripe(window.process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const calculateAge = (dob) => {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return { age: age, plurial: age > 1 ? 's' : '' };
};

const getLanguageLabel = (languageCode) => {
  return languageOptions.find((lang) => lang.value === languageCode)?.label;
}

const Profile = () => {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [subscription, setSubscription] = useState({});
  const [reloadData, setReloadData] = useState(true);
  const [modalMessage, setModalMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const { reloadUser } = useAuth();


  useEffect(() => {
    let intervalId = null;

    const fetchUserData = async () => {
      setLoading(true);
      try {
        const data = await getUserDetails();
        setUserDetails(data);
        pollSubscriptionStatus();
        if (data.subscriptionStatus === 'pending') {
          intervalId = setInterval(pollSubscriptionStatus, 3000);
          setTimeout(() => clearInterval(intervalId), 5 * 60 * 1000); //clear polling after 5 minutes of polling
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
      setLoading(false);
    };

    const pollSubscriptionStatus = async () => {
      try {
        const subScriptionData = await getMySubscriptionAndQuota();

        setSubscription(subScriptionData);

        // Stop polling if status is active
        if (subScriptionData.subscriptionStatus === 'active') {
          clearInterval(intervalId);
        }
      } catch (err) {
        console.error('Failed to fetch subscription status', err);
      }
    };

    if (reloadData) {
      fetchUserData();
      setReloadData(false);
    }
  }, [reloadData]);

  useEffect(() => {
      if (modalMessage) {
          const timer = setTimeout(() => setModalMessage(null), 10000); // 10 seconds
          return () => clearTimeout(timer);
      }
  }, [modalMessage]);

  const handleEditClick = (field) => {
    setEditingField(field);
    setIsModalOpen(true);
  };

  const handleCloseModal = (reload = false) => {
    setIsModalOpen(false);
    setEditingField(null);
    setError(null);
    if (reload) {
      reloadUser();
      setReloadData(true);
    }
  };

  const handleSave = async (updatedValue) => {
    try {
      setLoading(true);
      const updatedUser = await updateUserDetails(updatedValue);
      if (updatedValue.email && updatedValue.email !== userDetails.email) {
        setMessage(t('profile.confirmEmailAddress', {email: updatedValue.email}));
      }
      if (updatedValue.preferredLanguage && updatedValue.preferredLanguage !== userDetails.preferredLanguage) {
        i18n.changeLanguage(updatedValue.preferredLanguage.split("-")[0]);
      }
      setUserDetails(updatedUser);
      handleCloseModal();
      setModalMessage(t('profile.profileUpdated'));
    } catch(error) {
      if (error.response && error.response.status === 409) {
        setError(t('profile.errorEmailExisted'));
      } else if (error.response && error.response.status === 400) {
        const response = error.response.data;
        if (response.message === 'Invalid email') {
          setError(t('profile.errorInvalidEmail'));
        } else {
          setError(t('profile.errorInvalidRequest'));
        }
      } else {
        setError(t('profile.updatingFailed'));
      }
    } finally {
      setLoading(false);
    }
  }

  const handlePasswordSave = async (passwordData) => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t('profile.passwordNotMatch'));
      return;
    }
    try {
      setLoading(true);
      await updatePassword(passwordData);
      handleCloseModal();
      setModalMessage(t('profile.passwordUpdatedSuccessfully'));
    } catch (error) {
      if (error.response && error.response.status === 405) {
        setError(t('profile.invalidCurrentPassword'));
      } else if (error.response && error.response.status === 400) {
        const response = error.response.data;
        if (response.message === 'Invalid password') {
          setError(t('profile.errorInvalidPassword'));  
        } else {
          setError(t('profile.updatingPasswordFailed'));  
        }
      } else {
        setError(t('profile.updatingPasswordFailed'));  
      }
    } finally {
      setLoading(false);
    }
  };


  const renderEditForm = () => {
    if (!editingField) return null;

    switch (editingField) {
      case 'name':
        return (
          <UpdatingNameGenderDOB
            userDetails={userDetails}
            handleSave={handleSave}
            error={error}>
          </UpdatingNameGenderDOB>
        );
      case 'language':
        return (
          <UpdatingLanguage
            userDetails={userDetails}
            handleSave={handleSave}
            error={error}>
          </UpdatingLanguage>
        );
      case 'email':
        return (
          <UpdatingEmail
            userDetails={userDetails}
            handleSave={handleSave}
            error={error}>
          </UpdatingEmail>
        );
      case 'password':
        return (
          <UpdatingPassword
            handlePasswordSave={handlePasswordSave}
            error={error}>
          </UpdatingPassword>
        );
      case 'subscriptionLevel':
        return (
          <UpdatingSubscriptionLevel
            userDetails={userDetails}
            stripePromise={stripePromise}
            handleClose={handleCloseModal}
          >
          </UpdatingSubscriptionLevel>
        );
      default:
        return null;
    }
  };

  return (
    <div className="profile-page">

    {/* Loading screen */}
    {loading && <LoadingOverlay message={t('profile.loading')} />}

    <div className='user-details-component'>
      {error && <p className='form-error'>{error}</p>}
      {message && <p className="message">{message}</p>}
      <div className="user-details">
        <div className='user-details-flex-div'>
          <p><strong>{userDetails.name}</strong>, {userDetails.dateOfBirth ? t('profile.age', calculateAge(userDetails.dateOfBirth)) : t('profile.noAge')}{(userDetails.gender && userDetails.gender !== 'not communicated' && t(`profile.${userDetails.gender}`))}</p>
          <button className="edit-btn" onClick={() => handleEditClick('name')}>
            <i className="fas fa-edit"></i>
          </button>
        </div>
        <div className='user-details-flex-div'>
          <p><strong>{t('profile.preferredLanguage')}:</strong>&nbsp; {getLanguageLabel(userDetails.preferredLanguage)}</p>
          <button className="edit-btn" onClick={() => handleEditClick('language')}>
            <i className="fas fa-edit"></i>
          </button>
        </div>
        <div className='user-details-flex-div'>
          <p><strong>{t('profile.email')}:</strong>&nbsp; {userDetails.email}</p>
          <button className="edit-btn" onClick={() => handleEditClick('email')}>
            <i className="fas fa-edit"></i>
          </button>
        </div>
        {userDetails.changingEmail && <p className='form-error'>{userDetails.changingEmail} {t('profile.changingEmailInfo')}</p>}
        <div className='user-details-flex-div'>
          <p><strong>{t('profile.password')}:</strong>&nbsp; **********</p>
          <button className="edit-btn" onClick={() => handleEditClick('password')}>
            <i className="fas fa-edit"></i>
          </button>
        </div>
        <div className='user-details-flex-div'>
          <p><strong>{t('profile.subscriptionLevel')}</strong>&nbsp; {t(`subscription.${subscription.subscriptionLevel}`)}</p>
          <button className="edit-btn" onClick={() => handleEditClick('subscriptionLevel')}>
            <i className="fas fa-edit"></i>
          </button>
        </div>
{/*         {subscriptionStatusLoading && (
          <p className='user-details-subscription-status-loading'>{t('subscription.loadingStatus')}</p>
        )}
 */}    {subscription.subscriptionStatus === 'loading' && (
          <p className='user-details-subscription-pending'>{t('subscription.pendingStatus')}</p>
        )}
        {subscription.subscriptionLevel !== 'free' && subscription.subscriptionEndDate && (
          <p className='user-details-subscription-end-date'>{t('subscription.subscriptionEndDate', {subscriptionEndDate: formatDate(subscription.subscriptionEndDate)})}</p>
        )}
        {/* <p><strong>{t('profile.subscriptionSince')}:</strong> {userDetails.subscriptionStartDate ? new Date(userDetails.subscriptionStartDate).toLocaleDateString() : 'N/A'}</p> */}
        <div className='user-details-flex-div'>
          <p>{t('profile.storiesCreatedThisWeek', {plurial: subscription.storiesGeneratedThisWeek > 1 ? 's' : ''})}</p>
          <p className='right-aligned no-margin-right'>
            <span className='right-aligned-item'>{subscription.storiesGeneratedThisWeek}/{subscription.weeklyLimit}</span>
          </p>
        </div>
      </div>


    </div>

    <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
      {renderEditForm()}
    </Modal>

    <Modal isOpen={modalMessage} onClose={() => setModalMessage(null)}>
      <p>{t(modalMessage)}</p>
      <div className="modal-buttons">
          <button className="close-btn" onClick={() => setModalMessage(null)}>{t('profile.ok')}</button>
      </div>
    </Modal>


    </div>
  );
};

export default Profile;
